import {
  RecursivePartial,
  IAssistantAction,
  IAssistantTask,
  IThatAttachesTo,
  IWithTooltip,
  IWithBehaviors,
  IWithCallbacks,
  IWithBackdrop,
  IWithConditionalConf,
  IThatIsBasedOnTemplate,
  IWithTriggers,
  IWithBI,
  IWithCustomStage,
  IAssistantActionAndType,
} from './assistant';
import * as _ from 'lodash';
import {
  TourTooltipProps,
  TourTooltipArrowProps,
} from '../viewer/react/tooltip/TourTooltipWrapper';

export type callbackMethod = (
  task: IAssistantTask,
  action: IAssistantAction,
) => Promise<void>;

export const enum AssistantMenuProgress {
  beginner,
  intermediate,
}
export enum AssistantOperation {
  Next,
  Stop,
  Goto,
  Start,
  Complete,
  ForceStop,
  NoOp,
  Previous,
}
export enum TooltipOrientation {
  Vertical,
  Horizontal,
}
export enum ArrowPosition {
  Top,
  Bottom,
  Left,
  Right,
  Center,
}
export enum AssistantPositionY {
  Top,
  Center,
  Bottom,
}
export enum AssistantPositionX {
  Left,
  Center,
  Right,
}
export enum AlignPosition {
  Left,
  Right,
  Top,
  Bottom,
}
export enum AssistantDockStatus {
  Playing,
  Stopped,
  Disabled,
  Hidden,
}

export class AssistantAction {
  constructor(actionId = '') {
    this.conf.id = actionId;
  }

  static ELEMENT_SELECTOR = '$element';
  static BACKDROP = {
    ON: {
      enabled: true,
      opacity: 0.7,
    },
    INVISIBLE: {
      enabled: true,
      opacity: 0,
    },
    OFF: {
      enabled: false,
    },
    OPAQUE: {
      enabled: true,
      opacity: 1,
    },
  };
  static HOOKS = {
    SMART_ASSIST_BUBBLE_BUTTON: '[data-hook="bubble-button"]',
    BODY: 'body',
    $element: AssistantAction.ELEMENT_SELECTOR,
    TOP_BAR: '[data-hook="top-bar"]',
    DOCK: '.assistant-dock',
    DOCK_START: '.assistant-dock-start',
    BACKDROP: '.intro-backdrop',
    PREVIEW_TAB: '[data-hook="topbar-preview"]',
    ADD_TAB: '[data-hook="second-top-bar-add-section"]', //.add-tab',
    PUBLISH_TAB: '[data-hook="topbar-publish"]',
    ADD_SECTION_TAB: '[data-hook="add-section-button"]',
    ADD_PAGE_TAB: '[data-hook="add-page-button"]',
    ADD_APP_TAB: '[data-hook="add-app-button"]',
    PAGE_TAB_MENU_ITEM: '.pages-dropdown .sub-menu-item',
    DESIGN_TAB: '[data-hook="design-tab-drop-down-section"]',
    DESIGN_MENU_THEMES_OPTION: '[data-hook="open-themes-button"]',
    DESIGN_MENU_COLORS_OPTION: '[data-hook="open-colors-button"]',
    DESIGN_MENU_FONTS_OPTION: '[data-hook="open-fonts-button"]',
    DESIGN_MENU_ANIMATIONS_OPTION: '[data-hook="open-animations-button"]',
    DESIGN_MENU_PAGE_DESIGN_OPTION: '[data-hook="open-fpd-button"]',
    FAKE_BROWSER_OPTIMIZE_DESIGN: '[data-hook="optimize-design"]',
    EXIT_PREVIEW_BUTTON: '.tour-element-preview-back',
    KIT_OPTION: 'design-panel .kit-option',
    SELECTED_KIT_OPTION: 'design-panel .kit-option.selected',
    KIT_OPTION_CUSTOMISE: 'design-panel .kit-option.selected .customise-button',
    COLORS_PANEL: 'kit-colors cc-panel-frame',
    COLORS_CONTAINER: '.colors-container',
    COLORS_CONTAINER_NEW_COLORATION: '.color-palette > *',
    MAIN_COLOR_NEW_COLORATION: 'p21-color-tag [data-hook=color-tag]',
    MAIN_COLOR_DIALOG_NEW_COLORATION: '.colors-dialog',
    MAIN_COLOR_DIALOG_CANCEL_BUTTON_NEW_COLORATION:
      '.colors-dialog [data-hook="palette-color-dialog-cancel"]',
    MAIN_COLOR_DIALOG_CONFIRM_BUTTON_NEW_COLORATION:
      '.colors-dialog [data-hook="palette-color-dialog-confirm"]',
    MAIN_COLOR_DIALOG_COLOR_TAG: '.colors-dialog .p21-color-tag',
    KIT_PALETTE_NEW_COLORATION: 'kit-palette .p24-palette:not(.-selected)',
    ADVANCED_EDITING_NEW_COLORATION: 'coloration-panel .p18-panel-footer',
    COLORS: 'kit-colors .colors-container .colors',
    DESIGN_PANEL: 'design-panel',
    DESIGN_PANEL_KIT: 'design-panel-kit',
    PREVIEW_PADDING: '.preview-padding',
    PREVIEW_LIST: '.preview-list',
    PREVIEW_SECTION: '.preview-section-loading',
    PREVIEW_OVERLAY: '.preview-overlay',
    PREVIEW_OVERLAY_WITH_NOT_BLANK_PAGE:
      '.preview-list-container [role=button]:not(.page-blank-overlay)',
    PREVIEW_CONTAINER: '.preview-container',
    PREVIEW_CONTENT: '.preview-content',
    OVERLAY_CONTAINER: '.overlay-container',
    OVERLAY_SECTION_NOT_TPA: '.list-span-overlay:not(.tpa-span)',
    OVERLAY_SECTION_WITH_PRESETS: '.list-span-overlay.has-presets',
    PANEL_CLOSE_BUTTON: '.p16-panel-header [data-hook=close-button]',
    PANEL_HELP_BUTTON: '.p16-panel-header [data-hook=help-button]',
    PANEL_BACK_BUTTON: '.p16-panel-header [data-hook=return-button]',
    TEXT_MANAGER_LINK_IMPORTED:
      'button[cc-tooltip="text-manager-link-imported"]',
    RETURN_SUB_BUTTON: '[data-hook="return-sub-button"]',
    TEXT_MANAGER_DIALOG: '.text-manager-dialog',
    CUSTOMIZE_BUTTON: '[data-hook="customize-button"]',
    NODE_EDITOR: '.node-editor .cc-panel-frame.cc-container',
    NODE_EDITOR_BUTTON_FIELD: (nodeId) => {
      let selector: string;

      const buttons = ['field-button', 'field-button-list'];
      const selectorBuilder = (dataHook) =>
        `.node-editor .cc-panel-frame.cc-container field-editor[node-id="${nodeId}"] button-field-editor [data-hook="${dataHook}"]`;
      selector = buttons.map(selectorBuilder).join(',');

      return selector;
    },
    NODE_EDITOR_BUTTON_FIELD_TOGGLE_SHOW_BUTTON: (nodeId) =>
      `.node-editor .cc-panel-frame.cc-container field-editor[node-id="${nodeId}"] button-field-editor [data-hook="toggle-button"]`,
    NODE_EDITOR_BUTTON_FIELD_LINK_BUTTON: (nodeId) =>
      `.node-editor .cc-panel-frame.cc-container field-editor[node-id="${nodeId}"] button-field-editor [data-hook="c2e-button"]`,
    NODE_EDITOR_SOCIALS_FIELD:
      '.node-editor .cc-panel-frame.cc-container [data-hook="social-buttons"] > [data-hook="label"]',
    NODE_EDITOR_SOCIALS_FIELD_TOGGLE:
      '.node-editor .cc-panel-frame.cc-container [data-hook="social-buttons"] > [data-hook="label"] [data-hook="toggle-button"]',
    NODE_EDITOR_EMAIL_FIELD:
      '.node-editor .cc-panel-frame.cc-container [data-hook="field-text-input"] > [data-hook="label"]',
    LINK_PANEL_X: 'link-editor-panel .cc-icon-close-12',
    LINK_PANEL_CANCEL: 'link-editor-panel [data-hook="link-editor-cancel"]',
    LINK_PANEL_DONE: 'link-editor-panel [data-hook="link-editor-done"]',
    SOCIAL_PANEL_CONTENT: 'social-panel .cc-panel-body',
    PANEL_BODY: '[data-hook="panel-body"]',
    SOCIAL_PANEL_HEADER: 'social-panel .p16-panel-header',
    SOCIAL_PANEL_LIST: 'social-panel social-panel-inner',
    // when merge cardsToPanels remove the first rule
    SOCIAL_PANEL_X:
      'social-panel [data-hook="close-button"], single-panel [data-hook="close-button"]',
    TEXT_MANAGER_CLOSE_PANEL: 'text-manager-dialog [data-hook="close-button"]',
    LEFT_PANEL: '.builder-panel-inner',
    LEFT_PANEL_CONTENT: '.builder-panel-inner .content',
    PRESETS_CONTAINER: '.builder-left-panel .preview-list-wrap',
    PRESET: '[data-hook=preview]',
    PAGE_TAB: '.top-bar-section .page-tab',
    PAGE_TAB_TEMPORARY: '[data-hook="second-top-bar-pages-panel"]',
    PAGE_TAB_SECOND_TOPBAR: '[data-hook="second-top-bar-pages-panel"]',
    MANAGE_PAGES: '[data-hook="manage-pages"]',
    ADD_PAGE_BUTTON: '.add-page-button',
    PAGE_PANEL_FOOTER: '.pages-panel-inner-footer',
    UNSELECTED_PAGE_ITEMS: '.tree-node:not(.selected-page)',
    ADD_SECTION_PANEL: '.add-section',
    ADD_SECTION_PANEL_LABEL: '.add-section [label="Add Section"]',
    BROWSER_TOOLBAR: '.browser-toolbar',
    CONNECT_DOMAIN_BUTTON:
      '[data-hook="fake-browser-bar"] [data-hook="label"], .sugget-domain-cta',
    CONNECT_DOMAIN_BUTTON_NEW_TOP_BAR:
      '.assistant-connect-domain [data-hook="suggest-domain-cta"],[data-hook="fake-browser-connect-domain"]',
    PAGE_SETTINGS_BUTTON:
      '.pages-panel-inner .menu-list .tree-node.selected-page .settings-button',
    PAGES_PANEL: 'pages-panel .pages-panel-inner',
    PAGES_PANEL_SPLIT: 'pages-panel-split .pages-panel-inner',
    PAGE_TRANSITION_DROPDOWN: 'pages-panel [data-hook="p3-md-select"]',
    PAGE_SEO_MENU_ITEM:
      'md-dialog cc-menu-item[icon=cc-icon-seo] [data-hook="menu-item"]',
    SEO_PANEL: 'seo-panel',
    SEO_PANEL_PAGE_DESCRIPTION_FIELD: 'seo-panel c2-text-input:nth-of-type(2)',
    RECOMMENDED_SECTION: '.tour-element-recommended-section .section-button',
    NEXT_RECOMMENDED_SECTION:
      '.tour-element-recommended-section.selected + .tour-element-recommended-section .section-button',
    OVERLAY_SWITCH_LAYOUT_BUTTON: '.tour-element-switch-layout',
    OVERLAY_SECTION_EDIT_BUTTON: '[data-hook="edit-button"]',
    OVERLAY_MORE_ACTIONS_BUTTON: '.tour-more-actions-panel',
    OVERLAY_DRAG_HANDLE: '.tour-drag-handle',
    OVERLAY_EDIT_SECTION_BODY: '[data-hook="default-transclude"]',
    OVERLAY_EDIR_SECTION_HEADER: '.cc-headline',
    INSERT_SECTION_BUTTON: '.insert',
    TOOLTIP_BUTTON: '.tour-button',
    BLANK_PAGE_OVERLAY: '.page-blank-overlay',
    ANIMATION_CONTAINER: '.animations-container',
    ANIMATION_TIP: '.animations-container .animations-tip',
    CHANGE_ANIMATION: '[data-hook="animation-container"]',
    PREVIEW_ANIMATION: '[data-hook="preview-animation"]',
    ANIMATION_DROPDOWN_EXCLUDE_NO_ANIMATION:
      '._md-select-menu-container [data-hook=dropdown-list-item]:not(.no-animation)',
    ANIMATION_DROPDOWN_NO_ANIMATION:
      '._md-select-menu-container [data-hook=dropdown-list-item].no-animation',
    PAGES_PREVIEW: '#PAGES_CONTAINERscreenWidthBackground',
    CLOSE_PREVIEW: '.tour-element-preview-back',
    ANIMATION_CONTROL: '[data-hook="animations-control"]',
    ANIMATION_THUMBNAILS: '[data-hook="animation-thumbnails"]',
    ANIMATION_THUMBNAILS_EXCLUDE_NO_ANIMATION:
      '[data-hook="animation-thumbnails"] .c20-graphic-radio:not(:first)',
    ANIMATION_THUMBNAILS_NO_ANIMATION:
      '[data-hook="animation-thumbnails"] .c20-graphic-radio:first',
    SITE_DESIGN_DROPDOWN_MENU: '[data-hook="site-design-dropdown-menu"]',
    FONT_THUMBNAIL: '[data-hook="font-thumbnail"]',
    FONT_PANEL: 'font-panel .font-panel',
    FONT_PANEL_NG: 'font-panel-ng .cc-panel-frame',
    RETURN_BUTTON: '[data-hook="return-button"]',
    FONT_SIZE_SLIDER: '[data-hook="slider"].font-size-slider-control',
    CUSTOMIZE_DESIGN_BUTTON: '.customize-design-container .customize-design',
    HEADER_OVERLAY: '.header-overlay',
    FOOTER_OVERLAY: '.footer-overlay',
    SELECTED_PREVIEW_SECTION: '.preview-section.selected',
    SECTION_DESIGN_MENU_HOOK: '[data-hook="section-design-menu"]',
    MENU_ITEM: '.customize-design-container.selected cc-menu-item',
    BACKGROUND_BUTTON: '[data-hook="background-button"]',
    MEDIA_PREVIEW: '[data-hook="preview-content"]',
    MEDIA_CHOOSER_PREVIEW: 'media-chooser [data-hook="preview-content"]',
    MEDIA_SETTINGS_REPLACE_BUTTON: '[data-hook="replace-button"]',
    MEDIA_BUTTON: '[data-hook="media-button"]',
    WELCOME_PREVIEW_TOUR_BUTTON: '[data-hook="welcome-preview-tour-button"]',
    COLOR_BUTTON: '[data-hook="color-button"]',
    FONT_BUTTON: '[data-hook="font-button"]',
    ANIMATIONS_BUTTON: '[data-hook="animations-button"]',
    ORGANIZE_MEDIA_BUTTON: '[data-hook="organize-media-button"]',
    CC_PANEL_FRAME: '.cc-panel-frame',
    BACKGROUND_PREVIEW: '[data-hook="background"]:not(.-selected)',
    HEADERBG_BUTTON: '[data-hook="headerbg-button"]',
    LISTBG_BUTTON: '[data-hook="listbg-button"]',
    COLORATION_MODE_SELECTOR: 'coloration-mode-selector',
    SEGMENT_OVERLAY_CLASS: '.segment-overlay',
    GO_TO_SITE_COLORS: '[data-hook="goto-site-colors"]',
    GO_TO_SITE_FONTS: '[data-hook="goto-fonts-panel"]',
    GO_TO_SITE_ANIMATION: '[data-hook="goto-animation-panel"]',
    CHANGE_SECTION_HEIGHT_BUTTON: '[data-hook="height-button"]',
    SECTION_HEIGHT_CLOSE_BUTTON: '.action-button.close',
    GO_TO_SITE_DESIGN: '[data-hook="goto-site-design"]',
    SITE_DROPDOWN: '[data-hook="site-dropdown"]',
    SITE_TAB: '[data-hook="site-dropdown"]',
    SITE_TAB_ITEMS_EXCEPT_SEO_WIZ: '[data-hook="site-tab"] li:not(.seo-wiz)',
    SITE_DROPDOWN_ITEMS_EXCEPT_SEO_WIZ:
      '[data-hook="site-dropdown"] li:not(.seo-wiz)',
    SITE_DROPDOWN_SEO_WIZ: '[data-hook="seo-wiz"]',
    SITE_DROPDOWN_ITEMS_EXCEPT_BUSINESS_DETAILS:
      '[data-hook="site-dropdown"] li:not(.change-business-details)',
    SITE_DROPDOWN_BUSINESS_DETAILS: '[data-hook="change-business-details"]',
    DIALOG_BACKDROP: '.md-dialog-container',
    IMAGE_UPSCALE_MODAL_APPLY: '[data-hook="image-upscale-modal-cancel-btn"]',
    IMAGE_UPSCALE_MODAL_CANCEL: '[data-hook="image-upscale-modal-apply-btn"]',
  };
  static EVENTS = {
    ACTION_START: 'assistant.action.start',
    ACTION_COMPLETED: 'assistant.action.completed',
    TASK_START: 'assistant.task.start',
    TASK_COMPLETE: 'assistant.task.complete',
    TASK_QUIT: 'assistant.task.quit',
    TASK_SKIP: 'assistant.task.skip',
    TASK_TIMEOUT: 'assistant.task.timeout',
    MANUAL_OR_AUTO_CHOSE_MANUAL: 'assistant.action-type.manualauto.manual',
    MANUAL_OR_AUTO_CHOSE_AUTO: 'assistant.action-type.manualauto.auto',
    KEEP_TRYANOTHER_CHOSE_KEEP: 'assistant.action-type.keeptryagain.keep',
    KEEP_TRYANOTHER_CHOSE_TRYANOTHER:
      'assistant.action-type.keeptryagain.tryagain',
    OPEN_MENU: 'assistant.menu.open',
    CLOSE_MENU: 'assistant.menu.close',
    PROGRESS_BAR_ACTION_IN_PROGRESS: 'assistant.progress.action-inprogress',
    PROGRESS_BAR_ACTION_COMPLETED: 'assistant.progress.action-completed',
  };
  static TEMPLATES: { [key: string]: RecursivePartial<IAssistantAction> } = {
    SINGLE_BUTTON: {
      tooltip: {
        buttons: {
          BUTTON1: {
            intent: {
              operation: AssistantOperation.Next,
            },
          },
        },
      },
    },
    MANUAL_OR_AUTO: {
      tooltip: {
        buttons: {
          AUTO: {
            label: 'tour.button.auto',
            intent: {
              operation: AssistantOperation.Next,
              enableAutoplay: true,
            },
            BI: {
              event: AssistantAction.EVENTS.MANUAL_OR_AUTO_CHOSE_AUTO,
            },
          },
          MANUAL: {
            label: 'tour.button.manual',
            intent: {
              operation: AssistantOperation.Next,
              disableAutoplay: true,
            },
            BI: {
              event: AssistantAction.EVENTS.MANUAL_OR_AUTO_CHOSE_MANUAL,
            },
          },
        },
      },
    },
    KEEP_OR_ANOTHER: {
      additionalHighlightedElements: [AssistantAction.HOOKS.PREVIEW_CONTAINER],
      disabledElements: [
        AssistantAction.HOOKS.INSERT_SECTION_BUTTON,
        AssistantAction.HOOKS.OVERLAY_CONTAINER,
      ],
      onEnter: {
        stopAutoplay: true,
      },
      waitForSpinner: true,
      exitTriggers: [
        {
          element: AssistantAction.HOOKS.BACKDROP,
          intent: {
            operation: AssistantOperation.Complete,
          },
        },
      ],
      tooltip: {
        disabled: false,
        buttons: {
          TRY_ANOTHER: {
            label: 'tour.button.try-another',
            intent: {
              operation: AssistantOperation.Goto,
            },
            BI: {
              event: AssistantAction.EVENTS.KEEP_TRYANOTHER_CHOSE_TRYANOTHER,
            },
          },
          KEEP_IT: {
            label: 'tour.button.keep-it',
            intent: {
              operation: AssistantOperation.Complete,
            },
            BI: {
              event: AssistantAction.EVENTS.KEEP_TRYANOTHER_CHOSE_KEEP,
            },
          },
        },
        closeable: {
          enabled: true,
          intent: {
            operation: AssistantOperation.Complete,
          },
        },
      },
      preserveBackground: true,
      backdrop: AssistantAction.BACKDROP.INVISIBLE,
      actionIfAuto: {
        tooltip: {
          buttons: {
            TRY_ANOTHER: {
              intent: {
                enableAutoplay: true,
              },
            },
          },
        },
      },
    },
  };
  private readonly conf: IAssistantAction = { data: {} };

  build(): IAssistantAction {
    return this.conf;
  }

  thatAttachesTo = (): IThatAttachesTo => {
    const attachToNamespace = {
      build: this.build.bind(this),
      and: this as IAssistantActionAndType,

      dock: () => {
        this.thatAttachesTo().hook(AssistantAction.HOOKS.DOCK);
        this.withTooltip().thatOpensUp();
        return attachToNamespace;
      },

      hook: (hook: string) => {
        this.mergeConf({
          element: {
            selector: hook,
          },
        });
        return attachToNamespace;
      },

      thatWaitForElementToStopMoving: () => {
        this.mergeConf({
          element: {
            waitForElementToStopMoving: true,
          },
        });
        return attachToNamespace;
      },

      withoutHighlight: () => {
        this.mergeConf({
          element: {
            withoutHighlight: true,
          },
        });
        return attachToNamespace;
      },

      onlyUnselectedElements: () => {
        if (this.conf && this.conf.element && this.conf.element.selector) {
          this.mergeConf({
            element: {
              selector: AssistantAction.unselected(this.conf.element.selector),
            },
          });
        }

        return attachToNamespace;
      },

      onlySelectedElements: () => {
        if (this.conf && this.conf.element && this.conf.element.selector) {
          this.mergeConf({
            element: {
              selector: `${this.conf.element.selector}.selected`,
            },
          });
        }
        return attachToNamespace;
      },

      randomElement: () => {
        this.mergeConf({
          element: {
            isRandom: true,
          },
        });
        return attachToNamespace;
      },
    };
    return attachToNamespace;
  };

  withTooltip = (): IWithTooltip => {
    const tooltipNamespace = {
      build: this.build.bind(this),
      and: this as IAssistantActionAndType,

      withReactComponent: (
        reactComponent: React.ComponentType<TourTooltipProps>,
      ) => {
        this.mergeConf({
          tooltip: {
            reactComponent,
          },
        });
        return tooltipNamespace;
      },

      withCustomArrowComponent: (
        customArrowComponent: React.ComponentType<TourTooltipArrowProps>,
      ) => {
        this.mergeConf({
          tooltip: {
            customArrowComponent,
          },
        });
        return tooltipNamespace;
      },

      thatIsDisabled: () => {
        this.mergeConf({
          tooltip: {
            disabled: true,
          },
        });
        return tooltipNamespace;
      },

      thatIsCloseOnScroll: () => {
        this.mergeConf({
          tooltip: {
            closeTooltipOnScroll: true,
          },
        });
        return tooltipNamespace;
      },

      withTemplate: (template: string) => {
        this.mergeConf({
          tooltip: {
            disabled: false,
            template,
          },
        });
        return tooltipNamespace;
      },
      withButton: (buttonId: string) => {
        this.mergeConf({
          tooltip: {
            buttons: {},
          },
        });

        const withButtonNamespace = {
          withButton: tooltipNamespace.withButton,
          build: this.build.bind(this),
          and: this as any,

          label: (label: string, translationOptions?: any) => {
            this.mergeConf({
              tooltip: {
                buttons: {
                  [buttonId]: {
                    label,
                    labelTranslationOptions: translationOptions,
                  },
                },
              },
            });
            return withButtonNamespace;
          },

          callback: (callback: Function) => {
            this.mergeConf({
              tooltip: {
                buttons: {
                  [buttonId]: {
                    callback,
                  },
                },
              },
            });
            return withButtonNamespace;
          },

          onClickGoTo: (goToIndex: number) => {
            this.mergeConf({
              tooltip: {
                buttons: {
                  [buttonId]: {
                    intent: {
                      operation: AssistantOperation.Goto,
                      goToIndex,
                    },
                  },
                },
              },
            });
            return withButtonNamespace;
          },

          goToNextStep: () => {
            this.mergeConf({
              tooltip: {
                buttons: {
                  [buttonId]: {
                    intent: {
                      operation: AssistantOperation.Next,
                    },
                  },
                },
              },
            });
            return withButtonNamespace;
          },

          goToPreviousStep: () => {
            this.mergeConf({
              tooltip: {
                buttons: {
                  [buttonId]: {
                    intent: {
                      operation: AssistantOperation.Previous,
                    },
                  },
                },
              },
            });
            return withButtonNamespace;
          },

          completeOnClick: () => {
            this.mergeConf({
              tooltip: {
                buttons: {
                  [buttonId]: {
                    intent: {
                      operation: AssistantOperation.Complete,
                    },
                  },
                },
              },
            });
            return withButtonNamespace;
          },
        };

        return withButtonNamespace;
      },

      withTitle: (title: string, titleTranslationOptions?: any) => {
        this.mergeConf({
          tooltip: {
            disabled: false,
            title,
            titleTranslationOptions,
          },
        });
        return tooltipNamespace;
      },

      withWidth: (width: string) => {
        tooltipNamespace.withCustomStyle({ width, maxWidth: width });
        return tooltipNamespace;
      },

      withHeight: (height: string) => {
        tooltipNamespace.withCustomStyle({ height, minHeight: height });
        return tooltipNamespace;
      },

      withBackgroundColor: (backgroundColor: string) => {
        tooltipNamespace.withCustomStyle({ backgroundColor });
        return tooltipNamespace;
      },

      withCustomStyle: (customStyle: { [key: string]: string }) => {
        this.mergeConf({
          tooltip: {
            disabled: false,
            customStyle,
          },
        });
        return tooltipNamespace;
      },

      withSuccessMessage: () => {
        this.mergeConf({
          tooltip: {
            successMessage: true,
          },
        });
        return tooltipNamespace;
      },

      withCloseButton: () => {
        this.mergeConf({
          tooltip: {
            closeButton: true,
          },
        });
        return tooltipNamespace;
      },

      withContent: (content, contentTranslationOptions?) => {
        this.mergeConf({
          tooltip: {
            disabled: false,
            content,
            contentTranslationOptions,
          },
        });
        return tooltipNamespace;
      },

      withImage: (image: string) => {
        this.mergeConf({
          tooltip: {
            disabled: false,
            image,
          },
        });
        return tooltipNamespace;
      },

      withCustomProp: (propName: string, propValue: any) => {
        const customProps = this.conf.tooltip.customProps;

        this.mergeConf({
          tooltip: {
            customProps: { ...customProps, [propName]: propValue },
          },
        });
        return tooltipNamespace;
      },

      withoutTrackChanges: () => {
        this.mergeConf({ tooltip: { dontTrackChanges: true } });
        return tooltipNamespace;
      },

      thatDoesntPreserveBackground: () => {
        this.mergeConf({
          preserveBackground: false,
        });
        return tooltipNamespace;
      },

      thatIsntHiddenAfterAction: () => {
        this.mergeConf({ tooltip: { dontHideOnExit: true } });
        return tooltipNamespace;
      },

      thatOrientsVertically: () => {
        this.mergeConf({
          tooltip: {
            orientation: TooltipOrientation.Vertical,
          },
        });
        return tooltipNamespace;
      },

      thatOpensAsModal: () => {
        this.mergeConf({
          isModal: true,
        });
        return tooltipNamespace;
      },
      thatHasCustomClasses: (classes: string[]) => {
        const customClasses =
          (this.conf && this.conf.tooltip && this.conf.tooltip.customClasses) ||
          [];
        this.mergeConf({
          tooltip: {
            customClasses: [...customClasses, ...classes],
          },
        });

        return tooltipNamespace;
      },
      thatOpensFullScreen: () => {
        const assistantTooltipFullscreenClass = 'assistant-tooltip-fullscreen';
        const customClasses =
          (this.conf && this.conf.tooltip && this.conf.tooltip.customClasses) ||
          [];
        this.mergeConf({
          isModal: true,
          tooltip: {
            customClasses: [...customClasses, assistantTooltipFullscreenClass],
          },
        });

        return tooltipNamespace;
      },

      thatOpensUnderneathSpinnerLayer: () => {
        const allowSpinnerClass = 'underneath-spinner-layer';
        const customClasses =
          (this.conf && this.conf.tooltip && this.conf.tooltip.customClasses) ||
          [];

        this.mergeConf({
          isModal: true,
          tooltip: {
            customClasses: [...customClasses, allowSpinnerClass],
          },
        });

        return tooltipNamespace;
      },

      thatOpensRight: ({ offsetX = 0, offsetY = 0 } = {}) => {
        this.mergeConf({
          tooltip: {
            arrowPosition: ArrowPosition.Left,
            positionX: AssistantPositionX.Right,
            offsetY,
            offsetX,
          },
        });
        return tooltipNamespace;
      },

      thatOpensLeft: ({ offsetX = 0, offsetY = 0 } = {}) => {
        this.mergeConf({
          tooltip: {
            arrowPosition: ArrowPosition.Right,
            positionX: AssistantPositionX.Left,
            offsetY,
            offsetX,
          },
        });
        return tooltipNamespace;
      },

      thatOpensUp: ({ offsetX = 0, offsetY = 0 } = {}) => {
        this.mergeConf({
          tooltip: {
            arrowPosition: ArrowPosition.Bottom,
            positionY: AssistantPositionY.Top,
            offsetY,
            offsetX,
          },
        });
        return tooltipNamespace;
      },

      thatOpensDown: ({ offsetX = 0, offsetY = 0 } = {}) => {
        this.mergeConf({
          tooltip: {
            arrowPosition: ArrowPosition.Top,
            positionY: AssistantPositionY.Bottom,
            offsetY,
            offsetX,
          },
        });
        return tooltipNamespace;
      },

      thatOpensInTheCenter: () => {
        this.mergeConf({
          tooltip: {
            positionY: AssistantPositionY.Center,
            positionX: AssistantPositionX.Center,
          },
        });
        return tooltipNamespace;
      },

      thatOpens: (tooltipPosition) => {
        this.mergeConf({
          tooltip: {
            arrowPosition: this.translateArrowPosition(
              tooltipPosition.arrowPosition,
            ),
            positionY: this.translateVerticalDirection(
              tooltipPosition.verticalDirection,
            ),
            positionX: this.translateHorizontalDirection(
              tooltipPosition.horizontalDirection,
            ),
            offsetY: tooltipPosition.offsetY || 0,
            offsetX: tooltipPosition.offsetX || 0,
            alignTo: this.translateAlignToPosition(tooltipPosition.alignTo),
          },
        });
        return tooltipNamespace;
      },
    };
    return tooltipNamespace;
  };

  withBehaviors = (): IWithBehaviors => {
    const behaviorsNamespace = {
      build: this.build.bind(this),
      and: this as IAssistantActionAndType,

      thatSkipsStepOnCondition: (condition) => {
        this.mergeConf({
          shouldSkipAction: condition,
        });
        return behaviorsNamespace;
      },

      thatStopsAutoplayOnEnter: () => {
        this.mergeConf({
          onEnter: {
            stopAutoplay: true,
          },
        });
        return behaviorsNamespace;
      },

      thatStartsAutoplayOnEnter: () => {
        this.mergeConf({
          onEnter: {
            startAutoplay: true,
          },
        });
        return behaviorsNamespace;
      },

      thatStartsAutoplayOnExit: () => {
        this.mergeConf({
          onExit: {
            startAutoplay: true,
          },
        });
        return behaviorsNamespace;
      },

      thatStopsAutoplayOnExit: () => {
        this.mergeConf({
          onExit: {
            stopAutoplay: true,
          },
        });
        return behaviorsNamespace;
      },

      thatGoesToIndexOnAutoPlay: (index: number) => {
        this.mergeConf({
          autoPlayGoToIndex: index,
        });
        return behaviorsNamespace;
      },

      thatClosesSidePanelOnExit: () => {
        this.mergeConf({
          onExit: {
            DOMEvents: [
              {
                element: AssistantAction.HOOKS.PANEL_CLOSE_BUTTON,
                trigger: 'click',
              },
            ],
          },
        });
        return behaviorsNamespace;
      },

      thatClosesSidePanelOnEnter: () => {
        this.mergeConf({
          onEnter: {
            DOMEvents: [
              {
                element: AssistantAction.HOOKS.PANEL_CLOSE_BUTTON,
                trigger: 'click',
              },
            ],
          },
        });
        return behaviorsNamespace;
      },

      thatWaitsForSpinner: () => {
        this.mergeConf({
          waitForSpinner: true,
        });
        return behaviorsNamespace;
      },

      thatRedirectsTo: (state: string = null) => {
        this.mergeConf({
          redirectToState: state,
        });
        return behaviorsNamespace;
      },

      thatWaitsForElementWithCondition: (condition: (element?) => boolean) => {
        this.mergeConf({
          waitForElementCondition: condition,
        });
        return behaviorsNamespace;
      },

      thatDoesntScrollToElement: () => {
        this.mergeConf({
          scrollToElement: false,
        });
        return behaviorsNamespace;
      },

      thatDelays: (delay: number) => {
        this.mergeConf({
          delayBeforeStartInMs: delay,
        });
        return behaviorsNamespace;
      },

      thatWaitsForElement: (hook: string) => {
        this.mergeConf({
          waitForElement: hook,
        });
        return behaviorsNamespace;
      },

      thatWaitsForExpression: (expression: () => boolean) => {
        const { waitForExpressions = [] } = this.conf;
        this.mergeConf({
          waitForExpressions: [...waitForExpressions, expression],
        });
        return behaviorsNamespace;
      },

      thatHasHint: (
        positionX: AssistantPositionX = AssistantPositionX.Center,
        positionY: AssistantPositionY = AssistantPositionY.Center,
      ) => {
        this.mergeConf({
          hint: {
            enabled: true,
            positionX,
            positionY,
          },
        });
        return behaviorsNamespace;
      },

      withAutoplayTimeoutOf: (timeout: number) => {
        this.mergeConf({
          autoplayTimeoutInMs: timeout,
        });
        return behaviorsNamespace;
      },

      thatAllowsDialogInterruptions: () => {
        this.mergeConf({
          allowDialogInterruptions: true,
        });
        return behaviorsNamespace;
      },
    };
    return behaviorsNamespace;
  };

  withCallbacks = (): IWithCallbacks => {
    const callbacksNamespace = {
      build: this.build.bind(this),
      and: this as IAssistantActionAndType,

      onExit: (
        callback: (
          task: IAssistantTask,
          action: IAssistantAction,
        ) => Promise<any> | void,
      ) => {
        this.mergeConf({
          onExit: {
            callback,
          },
        });
        return callbacksNamespace;
      },

      onEnter: (callback: callbackMethod, withLoader?: boolean) => {
        this.mergeConf({
          onEnter: {
            callback,
            withLoader,
          },
        });
        return callbacksNamespace;
      },

      onPostRenderAction: (callback: callbackMethod) => {
        this.mergeConf({
          onPostRenderAction: {
            callback,
          },
        });
        return callbacksNamespace;
      },
    };
    return callbacksNamespace;
  };

  withBackdrop = (): IWithBackdrop => {
    const backdropNamespace = {
      build: this.build.bind(this),
      and: this as IAssistantActionAndType,

      thatIsOn: () => {
        this.mergeConf({
          backdrop: AssistantAction.BACKDROP.ON,
        });
        return backdropNamespace;
      },

      thatIsOff: () => {
        this.mergeConf({
          backdrop: AssistantAction.BACKDROP.OFF,
        });
        return backdropNamespace;
      },

      thatIsInvisible: () => {
        this.mergeConf({
          backdrop: AssistantAction.BACKDROP.INVISIBLE,
        });
        return backdropNamespace;
      },

      thatIsOpaque: () => {
        this.mergeConf({
          backdrop: AssistantAction.BACKDROP.OPAQUE,
        });
        return backdropNamespace;
      },

      thatFadesOutOnExit: (fadeOutTime: number = 1000) => {
        this.mergeConf({
          backdrop: {
            fadeOutOnExit: true,
            fadeOutTime,
          },
        });
        return backdropNamespace;
      },
    };
    return backdropNamespace;
  };

  withConditionalConf = (): IWithConditionalConf => {
    const ifNamespace = {
      build: this.build.bind(this),
      and: this as IAssistantActionAndType,

      experimentIsEnabled: (experimentName: string, conf: IAssistantAction) => {
        this.mergeConf({
          experiments: {
            [experimentName]: conf,
          },
        });
        return ifNamespace;
      },

      isInAutoplayMode: (conf: IAssistantAction) => {
        this.mergeConf({
          actionIfAuto: conf,
        });
        return ifNamespace;
      },

      isInManualMode: (conf: IAssistantAction) => {
        this.mergeConf({
          actionIfManual: conf,
        });
        return ifNamespace;
      },

      customCondition: (
        conditionMethod: () => boolean,
        truthyConf: IAssistantAction,
        falsyConf?: IAssistantAction,
      ) => {
        if (!this.conf.conditions) {
          this.mergeConf({
            conditions: [],
          });
        }
        this.mergeConf({
          conditions: [
            ...this.conf.conditions,
            {
              conditionMethod,
              truthyConf,
              falsyConf,
            },
          ],
        });
        return ifNamespace;
      },
    };
    return ifNamespace;
  };

  thatIsBasedOnTemplate = (): IThatIsBasedOnTemplate => {
    const basedOnNamespace = {
      build: this.build.bind(this),
      and: this as IAssistantActionAndType,

      manualOrAuto: () => {
        this.baseTooltipOn('MANUAL_OR_AUTO');
        return basedOnNamespace;
      },

      keepOrAnother: (stepIndex: number) => {
        this.baseTooltipOn('KEEP_OR_ANOTHER');
        this.mergeConf({
          tooltip: {
            buttons: {
              TRY_ANOTHER: {
                intent: {
                  actionIndex: stepIndex,
                },
              },
            },
          },
        });
        return basedOnNamespace;
      },

      singleButtonTooltip: (
        label: string,
        operation: AssistantOperation = AssistantOperation.Next,
      ) => {
        this.baseTooltipOn('SINGLE_BUTTON');
        this.mergeConf({
          tooltip: {
            buttons: {
              BUTTON1: {
                label,
                intent: {
                  operation,
                },
              },
            },
          },
        });
        return basedOnNamespace;
      },
    };

    return basedOnNamespace;
  };

  withTriggers = (): IWithTriggers => {
    const actionsNamespace = {
      build: this.build.bind(this),
      and: this as IAssistantActionAndType,

      nextOnElementClick: (
        hook?: string,
        descendant?: string,
        dontWaitForElement?: boolean,
        callback?: Function,
        ignoreOriginalEvent: boolean = false,
      ) => {
        this.createExitTrigger({
          operation: AssistantOperation.Next,
          hook,
          descendant,
          dontWaitForElement,
          callback,
          ignoreOriginalEvent,
        });
        return actionsNamespace;
      },

      goToOnElementClick: (
        hook: string,
        goToIndex: number,
        descendant?: string,
        dontWaitForElement?: boolean,
        callback?: Function,
      ) => {
        this.createExitTrigger({
          operation: AssistantOperation.Goto,
          hook,
          descendant,
          goToIndex,
          dontWaitForElement,
          callback,
        });
        return actionsNamespace;
      },

      nextOnBackdropClick: () => {
        this.createExitTrigger({
          operation: AssistantOperation.Next,
          hook: AssistantAction.HOOKS.BACKDROP,
        });
        return actionsNamespace;
      },

      stopOnElementClick: (hook?: string, dontWaitForElement?: boolean) => {
        this.createExitTrigger({
          operation: AssistantOperation.Stop,
          hook,
          dontWaitForElement,
        });
        return actionsNamespace;
      },

      completeOnElementMouseDown: (hook?: string) => {
        this.createExitTrigger({
          operation: AssistantOperation.Complete,
          hook,
          trigger: 'mousedown',
        });
        return actionsNamespace;
      },

      completeOnElementClick: (
        hook?: string,
        descendant?: string,
        dontWaitForElement?: boolean,
        callback?: Function,
      ) => {
        this.createExitTrigger({
          operation: AssistantOperation.Complete,
          hook,
          descendant,
          dontWaitForElement,
          callback,
        });
        return actionsNamespace;
      },

      forceStopOnElementClick: (
        hook?: string,
        dontWaitForElement?: boolean,
      ) => {
        this.createExitTrigger({
          operation: AssistantOperation.ForceStop,
          hook,
          dontWaitForElement,
        });
        return actionsNamespace;
      },

      completeOnBackdropClick: () => {
        this.createExitTrigger({
          operation: AssistantOperation.Complete,
          hook: AssistantAction.HOOKS.BACKDROP,
        });
        return actionsNamespace;
      },

      stopOnBackdropClick: (callback?: () => void) => {
        this.createExitTrigger({
          operation: AssistantOperation.Stop,
          hook: AssistantAction.HOOKS.BACKDROP,
          callback,
        });
        return actionsNamespace;
      },
    };
    return actionsNamespace;
  };

  withBI = (): IWithBI => {
    const biNamespace = {
      build: this.build.bind(this),
      and: this as IAssistantActionAndType,

      withDescription: (description: string) => {
        this.mergeConf({
          BI: {
            description,
          },
        });
        return biNamespace;
      },
    };
    return biNamespace;
  };

  withCustomStage = (): IWithCustomStage => {
    const stageNamespace = {
      build: this.build.bind(this),
      and: this as IAssistantActionAndType,

      withAdditionalClass: (className: string, hook?: string) => {
        const additionalClassForElements =
          (this.conf && this.conf.additionalClassForElements) || [];
        this.mergeConf({
          additionalClassForElements: [
            ...additionalClassForElements,
            { hook, className },
          ],
        });
        return stageNamespace;
      },

      withDisabledElements: (hook: string) => {
        const disabledElements =
          (this.conf && this.conf.disabledElements) || [];
        this.mergeConf({
          disabledElements: [...disabledElements, hook],
        });
        return stageNamespace;
      },

      withAdditionalHighlight: (hook: string, backgroundColor?: string) => {
        const additionalHighlightedElements =
          (this.conf && this.conf.additionalHighlightedElements) || [];
        this.mergeConf({
          additionalHighlightedElements: [
            ...additionalHighlightedElements,
            hook,
          ],
          ...(backgroundColor && { customStyle: { backgroundColor } }),
        });
        return stageNamespace;
      },

      withBlinkingHighlight: (
        hook: string,
        padding?: string,
        customBlinkingHighlightColor?: RGBColor,
      ) => {
        const blinkingHighlightElements =
          (this.conf && this.conf.blinkingHighlightElements) || [];
        this.mergeConf({
          blinkingHighlightElements: [...blinkingHighlightElements, hook],
          ...(padding && { customStyle: { padding } }),
          ...(customBlinkingHighlightColor && { customBlinkingHighlightColor }),
        });
        return stageNamespace;
      },

      withDisablePointerEvents: () => {
        this.mergeConf({
          disablePointerEvents: true,
        });
        return stageNamespace;
      },
    };
    return stageNamespace;
  };

  withCustomConf(conf: RecursivePartial<IAssistantAction>) {
    this.mergeConf(conf);
    return this;
  }

  private mergeConf(conf: RecursivePartial<IAssistantAction>) {
    _.merge(this.conf, conf);
  }

  private baseTooltipOn(type: string) {
    const basedOnType = AssistantAction.TEMPLATES[type];
    if (!basedOnType) {
      throw new Error(`unknown base type ${type}`);
    }
    this.mergeConf(basedOnType);
    return this;
  }

  private createExitTrigger({
    operation,
    hook,
    descendant,
    goToIndex,
    trigger = 'click',
    dontWaitForElement,
    callback,
    ignoreOriginalEvent,
  }: {
    operation: AssistantOperation;
    hook?: string;
    descendant?: string;
    goToIndex?: number;
    trigger?: string;
    dontWaitForElement?: boolean;
    callback?: Function;
    ignoreOriginalEvent?: boolean;
  }) {
    const exitTrigger = {
      element: hook || AssistantAction.ELEMENT_SELECTOR,
      descendant,
      intent: {
        operation,
        goToIndex,
      },
      trigger,
      dontWaitForElement,
      callback,
      ignoreOriginalEvent,
    };
    const exitTriggers = (this.conf && this.conf.exitTriggers) || [];
    exitTriggers.push(exitTrigger);
    // fix https://jira.wixpress.com/browse/EDIX-14670
    // @ts-expect-error Type instantiation is excessively deep and possibly infinite.
    this.mergeConf({
      exitTriggers,
    });
  }

  private translateHorizontalDirection(
    horizontalDirection: string,
  ): AssistantPositionX | undefined {
    switch (horizontalDirection) {
      case 'left':
        return AssistantPositionX.Left;
      case 'right':
        return AssistantPositionX.Right;
      default:
        return;
    }
  }

  private translateVerticalDirection(
    verticalDirection: string,
  ): AssistantPositionY | undefined {
    switch (verticalDirection) {
      case 'up':
        return AssistantPositionY.Top;
      case 'down':
        return AssistantPositionY.Bottom;
      default:
        return;
    }
  }

  private translateAlignToPosition(alignTo: string): AlignPosition | undefined {
    switch (alignTo) {
      case 'top':
        return AlignPosition.Top;
      case 'bottom':
        return AlignPosition.Bottom;
      case 'left':
        return AlignPosition.Left;
      case 'right':
        return AlignPosition.Right;
      default:
        return;
    }
  }

  private translateArrowPosition(
    arrowPosition: string,
  ): ArrowPosition | undefined {
    switch (arrowPosition) {
      case 'top':
        return ArrowPosition.Top;
      case 'bottom':
        return ArrowPosition.Bottom;
      case 'left':
        return ArrowPosition.Left;
      case 'right':
        return ArrowPosition.Right;
      case 'center':
        return ArrowPosition.Center;
      default:
        return;
    }
  }

  static unselected(selector: string): string {
    return `${selector}:not(.selected)`;
  }
}
